import {ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation} from '@angular/core';
import {FieldError} from '../../../avisDePub/creer/services/field-error';
import {StepperBuilderService} from '../../../avisDePub/creer/services/stepper.builder.service';
import {ErreurMessages} from '../../models/component-messages/erreur-messages';
import {BehaviorSubject, combineLatest, Observable, of, Subject} from 'rxjs';
import {catchError, first, filter, switchMap, takeUntil, timeout} from 'rxjs/operators';
import {FormulaireServiceService} from '../../../formulaires/services/formulaire-service.service';
import {StepError} from '../../../avisDePub/creer/services/step-error';
import {StepWarning} from '../../../avisDePub/creer/services/step-warning';
import {EformDuplicableEntityService} from '../../../formulaires/eforms/services/eform-duplicable-entity.service';
import {DuplicableElementIdPartEnum} from '../../../formulaires/eforms/enum/duplicable-element-id-part.enum';

@Component({
  selector: 'app-form-errors',
  templateUrl: './form.errors.component.html',
  styleUrls: ['./form.errors.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FormErrorsComponent implements OnInit, OnChanges, OnDestroy {

  constructor(public stepBuilder: StepperBuilderService, public formulaireService: FormulaireServiceService,
              private cd: ChangeDetectorRef, private eformDuplicableEntityService: EformDuplicableEntityService,
              private ngZone: NgZone) {
  }

  // Sauvegarde de l'élément affiché + rechargement de l'avis + chargement de l'élément ciblé
  private static readonly ACCORDION_CHANGE_TIMEOUT_MS = 15000;
  // Délai maximal d'apparition du champ une fois l'élément duplicable chargé (garde-fou, pas une attente fixe)
  private static readonly FIELD_WAIT_TIMEOUT_MS = 5000;
  // Partagé par tous les bandeaux : un nouveau clic, un changement d'étape ou la fermeture d'un bandeau
  // annule le scroll encore en attente
  private static readonly cancelPendingScroll$ = new Subject<void>();

  @Input() stepid;
  @Input() linkForRouter: string;
  public erreurMessages = new ErreurMessages();

  warnings: BehaviorSubject<Array<FieldError>> = new BehaviorSubject([]);
  warningLength = 0;

  errors: BehaviorSubject<Array<FieldError>> = new BehaviorSubject([]);
  errorLength = 0;

  /**
   * Mapping entre les clés de l'enum DuplicableElementIdPartEnum et les types internes (_idScheme)
   * utilisés par le service EformDuplicableEntityService.
   * Les clés de l'enum sont utilisées pour identifier les patterns dans les fragments d'URL,
   * mais le service utilise les types _idScheme pour gérer l'état des accordéons.
   */
  private readonly enumKeyToInternalType: { [key: string]: string } = {
    'GR-Organisations': 'ORG',
    'GR-Organisations_TEMP': 'ORG',
    'GR-LotResult': 'RES',
    'GR-LotTender': 'TEN',
    'GR-TenderingParty': 'TPA',
    'GR-SettledContract': 'CON',
    'GR-Lot': 'LOT'
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.stepid) {
      // Changement d'étape : le champ attendu n'est plus celui que l'utilisateur veut voir
      if (!changes.stepid.firstChange) {
        FormErrorsComponent.cancelPendingScroll$.next();
      }
      this.formulaireService.formulaireResponse.emit();
    }
  }

  ngOnDestroy(): void {
    FormErrorsComponent.cancelPendingScroll$.next();
  }

  /**
   * PRB0053438 : amène l'utilisateur sur le champ en erreur, en ouvrant d'abord l'élément duplicable concerné si
   * besoin. Aucune durée n'est supposée : on attend le signal de chargement de l'élément, puis l'apparition du champ.
   */
  scrollToFragment(fragment: string, event?: MouseEvent): void {
    event?.preventDefault();
    FormErrorsComponent.cancelPendingScroll$.next();
    const duplicableInfo = this.extractDuplicableInfoFromFragment(fragment);
    const fieldId = this.toDisplayedFieldId(fragment);
    const needsAccordionChange = this.checkIfAccordionChangeNeeded(fragment);

    let duplicableEntityDisplayed$: Observable<unknown> = of(null);
    if (needsAccordionChange) {
      // Remis à false avant le trigger pour ne pas confondre avec le chargement de l'élément précédent
      this.eformDuplicableEntityService.isDataDuplicableEntityReady.next(false);
      duplicableEntityDisplayed$ = this.waitForDuplicableEntityDisplayed(duplicableInfo).pipe(
        catchError(() => {
          console.warn('Timeout en attendant le chargement de l\'élément duplicable, recherche du champ quand même');
          return of(null);
        })
      );
    }

    // Abonnement AVANT le trigger : le signal de chargement peut être émis de façon synchrone
    duplicableEntityDisplayed$.pipe(
      switchMap(() => this.waitForElement(fieldId, fragment)),
      takeUntil(FormErrorsComponent.cancelPendingScroll$)
    ).subscribe({
      next: element => this.scrollAndFocus(element),
      error: () => console.warn('Champ en erreur introuvable dans la page : ' + fieldId)
    });

    if (needsAccordionChange) {
      this.triggerDuplicableEntity(fragment);
    }
  }

  /**
   * Le fragment d'erreur contient l'index de l'élément duplicable (ex: Organization-Company_4 pour ORG-0005),
   * mais dans le DOM chaque élément ouvert réutilise le même template avec l'index 0.
   */
  private toDisplayedFieldId(fragment: string): string {
    let fieldId = fragment;
    Object.values(DuplicableElementIdPartEnum).forEach(pattern => {
      const regex = new RegExp(`(${this.escapeRegExp(pattern)})\\d+(_|$)`, 'g');
      fieldId = fieldId.replace(regex, `$10$2`);
    });
    return fieldId;
  }

  /**
   * PRB0054132 : quand le contrôle existait déjà à la création du champ (retour sur l'étape),
   * form-input suffixe l'id du champ par '_1' (voir idFromName) : on le cherche aussi sous ce nom.
   * À défaut de l'id exact, un champ de lot est recherché par le début de son id. L'id exact reste
   * prioritaire : la page de lot est déjà rendue au clic (pas d'ouverture d'accordéon pour les lots).
   */
  private findField(fieldId: string, fragment: string): HTMLElement | null {
    return document.getElementById(fieldId) || document.getElementById(fieldId + '_1')
      || this.findLotFieldByIdPrefix(fragment);
  }

  /**
   * Émet le champ dès qu'il est présent dans la page : immédiatement s'il y est déjà, sinon à la première
   * modification du DOM qui le fait apparaître (MutationObserver). Les attributs id sont aussi observés car Angular
   * peut les renseigner après l'insertion de l'élément. Échoue au-delà de FIELD_WAIT_TIMEOUT_MS.
   */
  private waitForElement(fieldId: string, fragment: string): Observable<HTMLElement> {
    return new Observable<HTMLElement>(subscriber => {
      const emitIfPresent = (): boolean => {
        const element = this.findField(fieldId, fragment);
        if (element) {
          subscriber.next(element);
          subscriber.complete();
        }
        return !!element;
      };
      if (emitIfPresent()) {
        return undefined;
      }
      // Hors zone Angular : chaque modification du DOM ne doit pas relancer une détection de changements
      const observer = this.ngZone.runOutsideAngular(() => new MutationObserver(() => emitIfPresent()));
      observer.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['id']});
      return () => observer.disconnect();
    }).pipe(
      timeout(FormErrorsComponent.FIELD_WAIT_TIMEOUT_MS)
    );
  }

  private scrollAndFocus(element: HTMLElement): void {
    element.scrollIntoView({block: 'center'});
    // preventScroll : le focus ne doit pas déplacer à nouveau la page
    this.getFocusTarget(element)?.focus({preventScroll: true});
  }

  /**
   * PRB0053438 : attend que l'élément duplicable ciblé soit affiché et chargé avant de scroller.
   *
   * Pour un accordéon (organisation, résultat, offre...), ouvrir un autre élément passe d'abord par la sauvegarde
   * de l'élément affiché puis par le rechargement de l'avis (cf. DuplicableElementComponent.toggleAccordion) :
   * cela dépasse régulièrement 3 secondes, et le scroll partait alors avant l'ouverture de l'élément (il fallait
   * recliquer). On attend donc que l'accordéon ciblé soit celui affiché ET que ses données soient chargées,
   * avec un délai de sécurité plus conséquent.
   *
   * Seuls les accordéons passent par ici : les lots et parties sont des pages du stepper, exclues par
   * checkIfAccordionChangeNeeded.
   */
  private waitForDuplicableEntityDisplayed(duplicableInfo: { key: string | null, index: number | null }): Observable<unknown> {
    const internalType = this.getInternalTypeFromEnumKey(duplicableInfo.key);
    const isReady$ = this.eformDuplicableEntityService.isDataDuplicableEntityReady;

    return combineLatest([isReady$, this.eformDuplicableEntityService.expanded$]).pipe(
      filter(([isReady, expandedMap]) => isReady === true && expandedMap.get(internalType) === duplicableInfo.index),
      first(),
      timeout(FormErrorsComponent.ACCORDION_CHANGE_TIMEOUT_MS)
    );
  }

  /**
   * PRB0053513 : une erreur localisée d'après la structure du lot (champ introuvable dans le formulaire, cf.
   * FormulaireServiceService.buildLocationForLotOrPartField) porte l'id GR-Lot_{lot}_{sousEtape}|{champ}_{lot},
   * sans les index de répétition que form-input ajoute à l'id du champ (ex: GR-Lot_0_2|BT-615-Lot_0_0_0).
   * On recherche donc le champ par le début de son id : même sous-étape et même champ.
   * Une seule page de lot (ou de partie) est rendue à la fois. Selon le chemin de navigation, ses id portent l'index
   * du lot ou 0 (cf. prefixId dans section.component.html) : on essaie les deux.
   * Certains champs ont un double masqué portant le même id : on privilégie l'élément affiché.
   * Limité aux lots et parties pour ne pas changer la navigation des autres erreurs.
   */
  private findLotFieldByIdPrefix(fragment: string): HTMLElement | null {
    const match = fragment.match(/^(GR-(?:Lot|Part))_(\d+)_(\d+\|.+)_\d+$/);
    if (!match) {
      return null;
    }
    const [, sectionId, lotIndex, sousEtapeEtChamp] = match;
    const candidats = [lotIndex, '0'].reduce((elements: HTMLElement[], index) => elements.concat(Array.from(
      document.querySelectorAll<HTMLElement>(`[id^="${CSS.escape(`${sectionId}_${index}_${sousEtapeEtChamp}`)}_"]`))), []);
    return candidats.find(element => !!element.offsetParent) || candidats[0] || null;
  }

  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Élément à focaliser pour que l'utilisateur puisse saisir directement : le champ lui-même s'il est un champ de
   * saisie, sinon son premier champ de saisie enfant, sinon l'élément s'il est focusable.
   */
  private getFocusTarget(element: HTMLElement): HTMLElement | null {
    const tagName = element.tagName.toUpperCase();
    if (tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA') {
      return element;
    }
    const focusableSelector = 'input:not([type="hidden"]), textarea, select, [contenteditable="true"]';
    const focusableChild = element.querySelector<HTMLElement>(focusableSelector);
    if (focusableChild) {
      return focusableChild;
    }
    return element.tabIndex >= 0 || tagName === 'BUTTON' || tagName === 'A' ? element : null;
  }

  /**
   * Convertit une clé d'enum en type interne pour le service
   */
  private getInternalTypeFromEnumKey(enumKey: string): string {
    return this.enumKeyToInternalType[enumKey] || enumKey;
  }

  /**
   * Extrait les informations de l'élément duplicable depuis le fragment
   * @param fragment Le fragment d'URL (ex: GR-Organisations-Section_0_0|BT-513-Organization-Company_1)
   * @returns Un objet avec la clé de l'élément duplicable et son index
   */
  private extractDuplicableInfoFromFragment(fragment: string): { key: string | null, index: number | null } {
    let key: string | null = null;
    let index: number | null = null;

    if (fragment.includes('|')) {
      const splittedName = fragment.split('|');
      const secondPart = splittedName[1];

      // Chercher quel pattern duplicable est présent dans le fragment
      // et extraire l'index qui suit ce pattern
      Object.entries(DuplicableElementIdPartEnum).forEach(([enumKey, patternValue]) => {
        if (secondPart && secondPart.includes(patternValue)) {
          key = enumKey.replace(/_TEMP$/, '');

          // ✅ FIX 2026-03-11: Extraire l'index en utilisant le pattern du duplicable
          // Le pattern contient un underscore à la fin (ex: "Organization-Company_")
          // L'index est le chiffre qui suit ce pattern
          const patternIndex = secondPart.indexOf(patternValue);
          if (patternIndex !== -1) {
            // Trouver le chiffre qui suit le pattern
            const afterPattern = secondPart.substring(patternIndex + patternValue.length);
            // Extraire les chiffres au début (avant tout autre caractère)
            const match = afterPattern.match(/^(\d+)/);
            if (match) {
              index = parseInt(match[1], 10);
              if (isNaN(index)) {
                index = null;
              }
            }
          }
        }
      });
    }

    return { key, index };
  }


  /**
   * Vérifie si un changement d'accordéon est nécessaire SANS déclencher le trigger.
   * Cette méthode est utilisée pour déterminer si on doit attendre le rechargement de l'accordéon.
   */
  private checkIfAccordionChangeNeeded(fragment: string): boolean {
    const duplicableInfo = this.extractDuplicableInfoFromFragment(fragment);
    const key = duplicableInfo.key;
    const index = duplicableInfo.index;

    // PRB0053513 : les lots sont des pages du stepper, pas des accordéons (aucun n'est enregistré sous la clé GR-Lot) :
    // attendre leur ouverture ne ferait qu'expirer le délai de sécurité de 3 secondes avant de scroller.
    if (key === 'GR-Lot') {
      return false;
    }
    if (key && index !== null) {
      const internalType = this.getInternalTypeFromEnumKey(key);
      const currentDisplayedIndex = this.eformDuplicableEntityService.getDisplayedElementIndex(internalType);
      return currentDisplayedIndex !== index;
    }
    return false;
  }

  /**
   * Déclenche le trigger pour ouvrir l'accordéon correspondant au fragment.
   * Cette méthode est appelée APRÈS s'être abonné au signal isDataDuplicableEntityReady.
   */
  private triggerDuplicableEntity(fragment: string): void {
    const duplicableInfo = this.extractDuplicableInfoFromFragment(fragment);
    const key = duplicableInfo.key;
    const index = duplicableInfo.index;

    if (key && index !== null) {
      this.eformDuplicableEntityService.triggerDuplicableEntity(key, index);
    }
  }

  public ngOnInit() {

    this.formulaireService.formulaireResponse.subscribe(
      () => {
        const warnings = this.getWarnings();
        this.warnings.next(warnings);
        this.warningLength = warnings.length;

        const errors = this.getErrors();
        this.errors.next(errors);
        this.errorLength = errors.length;
      }
    );

    this.stepBuilder.newError.subscribe((val) => {
      if (val) {
        const errors = this.getErrors();
        this.errors.next(errors);
        this.errorLength = errors.length;
        this.stepBuilder.newError.next(false);
      }
    });

    this.stepBuilder.newWarning.subscribe((val) => {
      if (val) {
        const warnings = this.getWarnings();
        this.warnings.next(warnings);
        this.warningLength = warnings.length;
        this.stepBuilder.newWarning.next(false);
      }
    });

  }

  public getErrors(): Array<FieldError> {
    let errors: Array<FieldError>;

    // Cas spéciaux avec SepSep (TypeFacturation, TypeIndexation, idAnnonceur)
    if (this.isSpecialSepSepCase()) {
      errors = this.handleSpecialSepSepCase();
    }
    // Cas avis nationaux avec pattern _STEP_ID_X_SepSep_... (ex: _STEP_ID_1_SepSep_Annonce_...)
    else if (this.isNationalNoticeCase()) {
      errors = this.handleNationalNoticeCase();
    }
    // Cas avec pattern _X_Y (ex: GR-Buyer_0_1, GR-Result_0_3)
    else if (this.hasMultipleNumericSegments() && !this.isSepSepSubSection()) {
      errors = this.handleMultipleNumericSegments();
    }
    // Cas simple avec un seul chiffre à la fin (ex: _STEP_ID_1)
    else if (this.hasSimpleNumericSuffix() && !this.isSepSepSubSection()) {
      errors = this.handleSimpleNumericSuffix();
    }
    // Cas normal : retourne uniquement les erreurs de la sous-section exacte
    else {
      errors = this.stepBuilder.getStepFieldsOnErrors(this.stepid);
    }

    // Tri des erreurs par index
    return this.sortErrorsByIndex(errors);
  }

  /**
   * Vérifie si le stepid est un cas spécial avec SepSep
   */
  private isSpecialSepSepCase(): boolean {
    return this.stepid == '_STEP_ID_SepSep_TypeFacturation' ||
      this.stepid == '_STEP_ID_SepSep_TypeIndexation' ||
      this.stepid == '_STEP_ID_SepSep_idAnnonceur';
  }

  /**
   * Gère les cas spéciaux avec SepSep (Facturation, Indexation, Annonceur)
   */
  private handleSpecialSepSepCase(): Array<FieldError> {
    if (this.stepid == '_STEP_ID_SepSep_TypeFacturation') {
      const facturationErrorList = this.extractValuesByKey(this.stepBuilder.stepFieldsOnErrors, '_SepSep_TypeFacturation');
      this.stepBuilder.facturationErrorCount = facturationErrorList.length;
      return facturationErrorList;
    } else if (this.stepid == '_STEP_ID_SepSep_TypeIndexation') {
      const indexationErrorList = this.extractValuesByKey(this.stepBuilder.stepFieldsOnErrors, '_SepSep_TypeIndexation');
      this.stepBuilder.indexationErrorCount = indexationErrorList.length;
      return indexationErrorList;
    } else if (this.stepid == '_STEP_ID_SepSep_idAnnonceur') {
      const idAnnonceurErrorList = this.extractValuesByKey(this.stepBuilder.stepFieldsOnErrors, '_SepSep_idAnnonceur');
      this.stepBuilder.annonceurErrorCount = idAnnonceurErrorList.length;
      return idAnnonceurErrorList;
    }
    return [];
  }

  /**
   * Vérifie si le stepid contient SepSep
   */
  private isSepSepSubSection(): boolean {
    return this.stepid.includes('SepSep');
  }

  /**
   * Vérifie si c'est un cas d'avis national avec pattern _STEP_ID_X_SepSep_...
   * Ex: "_STEP_ID_1_SepSep_Annonce_..." ou "lieuExecutionLivraison_STEP_ID_2_SepSep_..."
   */
  private isNationalNoticeCase(): boolean {
    return /_STEP_ID_\d+_SepSep_/.test(this.stepid);
  }

  /**
   * Gère les cas d'avis nationaux
   * Retourne uniquement les erreurs de cette étape spécifique
   */
  private handleNationalNoticeCase(): Array<FieldError> {
    // Pour les avis nationaux, chaque stepid est unique et correspond à une sous-section spécifique
    // On retourne donc uniquement les erreurs de ce stepid exact
    return this.stepBuilder.getStepFieldsOnErrors(this.stepid);
  }

  /**
   * Vérifie si le stepid a plusieurs segments numériques (ex: _0_1, _1_0)
   */
  private hasMultipleNumericSegments(): boolean {
    return /_\d+_\d+$/.test(this.stepid);
  }

  /**
   * Vérifie si le stepid a un simple suffixe numérique (ex: _1, _2)
   */
  private hasSimpleNumericSuffix(): boolean {
    return /_\d+$/.test(this.stepid);
  }

  /**
   * Gère les cas avec pattern _X_Y (ex: GR-Buyer_0_1 ou GR-Result_0_3)
   * Différencie les groupes duplicables des sous-sections distinctes
   */
  private handleMultipleNumericSegments(): Array<FieldError> {
    const parts = this.stepid.split('_');
    const secondLastNumber = parts[parts.length - 2];

    // Vérifie si le groupe a des sous-sections dans le stepper
    if (this.groupHasSections()) {
      // Sous-sections distinctes (ex: GR-Result) -> erreurs séparées
      return this.stepBuilder.getStepFieldsOnErrors(this.stepid);
    } else {
      // Groupe duplicable (ex: GR-Buyer) -> agrège les instances
      return this.aggregateErrorsForDuplicableGroup(parts, secondLastNumber);
    }
  }

  /**
   * Vérifie si le groupe a des sous-sections dans le stepper
   */
  private groupHasSections(): boolean {
    const groupName = this.extractGroupName();

    if (!groupName || !this.stepBuilder.stepper) {
      return false;
    }

    const stepperValue = this.stepBuilder.stepper.value;
    if (!stepperValue || !Array.isArray(stepperValue) || stepperValue.length === 0) {
      return false;
    }

    const step = stepperValue.find(s => s && s.eformName === groupName);
    return !!(step && step.sections && Array.isArray(step.sections) && step.sections.length > 0);
  }

  /**
   * Extrait le nom du groupe depuis le stepid (ex: "GR-Buyer", "GR-Result")
   */
  private extractGroupName(): string | null {
    // Gère deux cas : avec ou sans le préfixe _STEP_ID_
    // Ex: "_STEP_ID_GR-Buyer_0_0" ou "GR-Buyer_0_0"
    let groupNameMatch = this.stepid.match(/^_STEP_ID_([A-Za-z\-]+)_/);
    if (groupNameMatch) {
      return groupNameMatch[1];
    }

    // Si pas de préfixe _STEP_ID_, extrait directement le nom avant le premier underscore suivi de chiffres
    groupNameMatch = this.stepid.match(/^([A-Za-z\-]+)_\d+_\d+$/);
    return groupNameMatch ? groupNameMatch[1] : null;
  }

  /**
   * Agrège les erreurs pour un groupe duplicable
   */
  private aggregateErrorsForDuplicableGroup(parts: string[], secondLastNumber: string): Array<FieldError> {
    const stepBaseId = this.stepid.substring(0, this.stepid.lastIndexOf('_') + 1);
    const keys = Array.from(this.stepBuilder.stepFieldsOnErrors.keys());

    const matchingKeys = keys.filter(key => {
      if (!key.startsWith(stepBaseId)) { return false; }
      const keyParts = key.split('_');
      return keyParts.length === parts.length &&
        keyParts[keyParts.length - 2] === secondLastNumber;
    });

    if (matchingKeys.length > 1) {
      return this.collectErrorsFromKeys(matchingKeys);
    } else {
      return this.stepBuilder.getStepFieldsOnErrors(this.stepid);
    }
  }

  /**
   * Gère les cas simples avec un seul chiffre à la fin (ex: _STEP_ID_1)
   * Pour les avis nationaux, chaque _STEP_ID_X correspond à une page/étape distincte
   * On ne doit donc PAS agréger les erreurs
   */
  private handleSimpleNumericSuffix(): Array<FieldError> {
    // Pour les avis nationaux (_STEP_ID_1, _STEP_ID_2, etc.), chaque stepid est une page distincte
    // On retourne uniquement les erreurs de ce stepid exact
    return this.stepBuilder.getStepFieldsOnErrors(this.stepid);
  }

  /**
   * Collecte les erreurs depuis une liste de clés
   */
  private collectErrorsFromKeys(keys: string[]): Array<FieldError> {
    let allErrors: any[] = [];
    for (const key of keys) {
      const errors = this.stepBuilder.getStepFieldsOnErrors(key);
      if (errors.length > 0) {
        allErrors = allErrors.concat(errors);
      }
    }
    return allErrors;
  }

  /**
   * Trie les erreurs par ordre d'index numérique
   * Extrait les index depuis les fragments et trie les erreurs en conséquence
   */
  private sortErrorsByIndex(errors: Array<FieldError>): Array<FieldError> {
    if (!errors || errors.length === 0) {
      return errors;
    }

    return errors.sort((a, b) => {
      const indexA = this.extractIndexFromFragment(a.fieldName);
      const indexB = this.extractIndexFromFragment(b.fieldName);

      // Si les deux ont des index, comparer numériquement
      if (indexA !== null && indexB !== null) {
        return indexA - indexB;
      }

      // Si seulement A a un index, le mettre après
      if (indexA !== null && indexB === null) {
        return 1;
      }

      // Si seulement B a un index, le mettre après
      if (indexA === null && indexB !== null) {
        return -1;
      }

      // Si aucun n'a d'index, trier alphabétiquement par nom
      return (a.fieldName || '').localeCompare(b.fieldName || '');
    });
  }

  /**
   * Extrait l'index numérique depuis un fragment/nom de champ
   * Supporte différents formats: Organization_2, GR-Buyer_0_1, _STEP_ID_3, etc.
   */
  private extractIndexFromFragment(fieldName: string): number | null {
    if (!fieldName) {
      return null;
    }

    // Gère les cas avec pipe (|) pour les entités duplicables
    if (fieldName.includes('|')) {
      const parts = fieldName.split('|');
      if (parts[1] && parts[1].includes('_')) {
        const indexMatch = parts[1].match(/_(\d+)$/);
        if (indexMatch) {
          return parseInt(indexMatch[1], 10);
        }
      }
    }

    // Extrait le dernier nombre du nom de champ
    const matches = fieldName.match(/_(\d+)(?:_\d+)?$/);
    if (matches && matches[1]) {
      return parseInt(matches[1], 10);
    }

    return null;
  }

  public getWarnings(): Array<FieldError> {
    let warnings: Array<FieldError>;

    if (this.stepid == '_STEP_ID_SepSep_TypeFacturation') {
      const facturationWarningList = this.extractWarningValuesByKey(this.stepBuilder.stepFieldsOnWarnings, '_SepSep_TypeFacturation');
      this.stepBuilder.facturationWarningCount = facturationWarningList.length;
      warnings = facturationWarningList;
    } else if (this.stepBuilder.getStepFieldsOnWarnings(this.stepid).length > 0) {
      warnings = this.stepBuilder.getStepFieldsOnWarnings(this.stepid);
    } else {
      warnings = [];
    }

    // Tri des warnings par index également
    return this.sortErrorsByIndex(warnings);
  }

  extractValuesByKey(map: Map<string, StepError>, value: string): any[] {
    const result: Array<FieldError> = [];
    for (const [key, element] of map.entries()) {
      if (key?.includes(value)) {
        for (const val of element.errors) {
          result.push(val);
        }
      }
    }
    return result;
  }

  extractWarningValuesByKey(map: Map<string, StepWarning>, value: string): any[] {
    const result: Array<FieldError> = [];
    for (const [key, element] of map.entries()) {
      if (key?.includes(value)) {
        for (const val of element.warnings) {
          result.push(val);
        }
      }
    }
    return result;
  }
}
